'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { DataSkeleton } from '@/app/Components/DataSkeleton';
import { DIRECTORY_PAGE_SIZE, compensationSignals } from './selectors';
import {
  ArrowTopRightOnSquareIcon,
  BookmarkIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/app/Components/Button';
import { Field, Input, Select } from '@/app/Components/Field';
import { MobileFilterSheet } from '@/app/Components/MobileFilterSheet';
import Modal from '@/app/Components/Modal';
import {
  OperationalEvidence,
  OperationalHeader,
  OperationalPage,
  OperationalSource,
  OperationalState,
  OperationalStatus,
} from '@/app/Components/OperationalPage';
import { buttonClassName } from '@/app/Components/buttonStyles';
import type {
  DirectoryFilters,
  DirectoryOpportunity,
  NSFAwardDetail,
  ResearchOpportunitiesDirectoryViewModel,
} from './types';

export type DirectoryViewActions = {
  searchInput: string;
  setSearchInput(value: string): void;
  filtersOpen: boolean;
  setFiltersOpen(value: boolean): void;
  setFilter<K extends keyof DirectoryFilters>(key: K, value: DirectoryFilters[K]): void;
  applySearch(): void;
  clearFilters(): void;
  retry(): void;
  select(item: DirectoryOpportunity): void;
  closeDetail(): void;
};

export function ResearchOpportunitiesDirectoryView({ model, actions }: {
  model: ResearchOpportunitiesDirectoryViewModel;
  actions: DirectoryViewActions;
}) {
  const [saved, setSaved] = useState<string[]>([]);
  const [savedOnly, setSavedOnly] = useState(false);
  const [saveError, setSaveError] = useState('');
  useEffect(() => {
    try {
      const value: unknown = JSON.parse(localStorage.getItem('ss-research-shortlist-v1') || '[]');
      if (Array.isArray(value)) setSaved(value.filter((id): id is string => typeof id === 'string'));
    } catch { /* A blocked or invalid local store leaves the directory usable. */ }
  }, []);
  function toggleSaved(id: string) {
    const next = saved.includes(id) ? saved.filter(value => value !== id) : [...saved, id];
    try {
      localStorage.setItem('ss-research-shortlist-v1', JSON.stringify(next));
      setSaved(next);
      setSaveError('');
    } catch { setSaveError('Saving is unavailable in this browser. Please try again.'); }
  }
  if (model.state === 'loading') return <OperationalPage><OperationalHeader title="Research opportunities" /><DataSkeleton label="Loading Rutgers research snapshots" /></OperationalPage>;
  if (model.state === 'error') return <OperationalPage><OperationalState kind="error" title="Research opportunities unavailable" description={model.error} action={<Button onClick={actions.retry}>Retry</Button>} /></OperationalPage>;

  const { filters } = model;
  const activeFilterCount = countActiveFilters(filters);
  const hasChanges = activeFilterCount > 0 || Boolean(filters.searchQuery || actions.searchInput);
  const source = filters.sourceTab || 'rutgers';
  const sources = [
    { id: 'rutgers', label: 'Rutgers labs' },
    { id: 'reu-site', label: 'Summer programs (REU)' },
    { id: 'nsf-award', label: 'NSF awards' },
  ] as const;
  const rows = savedOnly ? (model.allResults ?? model.results).filter(item => saved.includes(item.id)) : model.results;
  const date = model.retrievedAt ? new Date(model.retrievedAt) : undefined;
  const dateLabel = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : undefined;

  return <OperationalPage data-route-data-state={model.state}>
    <OperationalHeader title="Research opportunities"
      description={`${model.sourceCounts?.rutgers ?? model.metrics[0]?.value ?? 0} Rutgers projects, plus summer programs and NSF funding records.`}
      actions={<div className="max-w-sm text-sm"><OperationalEvidence tone="warning">{source === 'rutgers' ? `Rutgers data${dateLabel ? ` as of ${dateLabel}` : ': date unavailable'}. Confirm openings with the lab.` : source === 'nsf-award' ? 'NSF awards describe funding, not student openings.' : 'Summer programs across institutions. Confirm current dates and eligibility on the source site.'}</OperationalEvidence></div>} />
    {model.state === 'partial' ? <OperationalState kind="partial" title="Rutgers projects remain available" description={model.error} /> : null}

    <nav aria-label="Research sources" className="grid grid-cols-3 gap-x-2 border-b border-line sm:flex sm:flex-wrap sm:gap-x-5">
      {sources.map(tab => <button key={tab.id} type="button" aria-pressed={source === tab.id}
        className={`min-h-12 border-b-2 px-1 py-3 text-xs sm:text-base ${source === tab.id ? 'border-[var(--ss-brand)] font-semibold text-content' : 'border-transparent text-content-muted'}`}
        onClick={() => { setSavedOnly(false); actions.setFilter('sourceTab', tab.id); }}>
        {tab.id === 'reu-site' ? <><span className="sm:hidden">Summer programs</span><span className="hidden sm:inline">{tab.label}</span></> : tab.label} <span className="ml-1 text-content-muted">{tab.id === 'rutgers' || model.optionalSources[tab.id === 'reu-site' ? 'reu' : 'nsf'].loaded ? model.sourceCounts?.[tab.id] : ''}</span>
      </button>)}
    </nav>

    <section aria-label="Search and filter research" className="space-y-3">
      <form className="flex gap-3" onSubmit={event => { event.preventDefault(); actions.applySearch(); }}>
        <Field label="Search research opportunities" hideLabel className="min-w-0 flex-1">
          {props => <Input {...props} type="search" value={actions.searchInput} placeholder="Title, professor, or topic" onChange={event => actions.setSearchInput(event.target.value)} />}
        </Field>
        <Button type="submit"><MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />Search</Button>
      </form>
      <div className="flex flex-wrap items-center gap-2">
        {source === 'rutgers' ? <>
          <Button variant={filters.paidOnly ? 'primary' : 'secondary'} aria-pressed={Boolean(filters.paidOnly)} onClick={() => actions.setFilter('paidOnly', !filters.paidOnly)}><CurrencyDollarIcon className="h-4 w-4" aria-hidden="true" />Paid</Button>
          <Button variant={filters.creditOnly ? 'primary' : 'secondary'} aria-pressed={Boolean(filters.creditOnly)} onClick={() => actions.setFilter('creditOnly', !filters.creditOnly)}>Credit</Button>
          <div className="hidden min-w-0 max-w-48 md:block"><DirectorySelect label="Campus" value={filters.campus} options={model.filterOptions.campuses} onChange={value => actions.setFilter('campus', value)} compact /></div>
          <div className="hidden min-w-0 max-w-64 md:block"><DirectorySelect label="Department" value={filters.department} options={model.filterOptions.departments} onChange={value => actions.setFilter('department', value)} compact /></div>
        </> : null}
        <Button variant="secondary" aria-expanded={actions.filtersOpen} onClick={() => actions.setFiltersOpen(!actions.filtersOpen)}><FunnelIcon className="h-4 w-4" aria-hidden="true" />More filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</Button>
        <Button variant="quiet" aria-pressed={savedOnly} onClick={() => setSavedOnly(!savedOnly)}><BookmarkIcon className="h-4 w-4" aria-hidden="true" /><span className="sr-only sm:not-sr-only">Saved on this device</span> ({saved.length})</Button>
        {hasChanges ? <Button variant="quiet" onClick={actions.clearFilters}>Clear filters</Button> : null}
        <div className="min-w-0 md:ml-auto"><Field label="Sort results" hideLabel>{props => <Select {...props} value={filters.sortBy} onChange={event => actions.setFilter('sortBy', event.target.value as DirectoryFilters['sortBy'])}><option value="relevance">Sort: Best match</option><option value="title">Sort: Title</option><option value="professor">Sort: Professor</option><option value="department">Sort: Department</option></Select>}</Field></div>
      </div>
      {actions.filtersOpen ? <div className="hidden border-t border-line pt-4 md:block"><DirectoryFilters model={model} actions={actions} /></div> : null}
      <OptionalSourceFeedback model={model} />
      {saveError ? <p role="alert" className="text-sm text-content-muted">{saveError}</p> : null}
    </section>

    <MobileFilterSheet isOpen={actions.filtersOpen} title="Research filters" summary={`${activeFilterCount} active filters`} onClose={() => actions.setFiltersOpen(false)}
      footer={<div className="grid grid-cols-2 gap-3"><Button variant="secondary" onClick={actions.clearFilters}>Clear</Button><Button onClick={() => actions.setFiltersOpen(false)}>Show results</Button></div>}>
      <DirectoryFilters model={model} actions={actions} />
    </MobileFilterSheet>

    <section aria-label="Research results">
      <h2 className="sr-only">Matching research opportunities</h2>
      <p className="border-b border-line pb-3 text-sm text-content-muted" role="status" aria-label="Result count">{savedOnly ? rows.length : model.totalResults} results · {sources.find(tab => tab.id === source)?.label}{filters.paidOnly ? ' · Paid' : ''}{filters.creditOnly ? ' · Credit' : ''}{savedOnly ? ' · Saved on this device' : ''}</p>
      {rows.length ? <div className="divide-y divide-line">{rows.map(item => <OpportunityRow key={item.id} item={item} query={filters.searchQuery} saved={saved.includes(item.id)} onSave={() => toggleSaved(item.id)} onOpen={() => actions.select(item)} />)}</div>
        : model.optionalSources[source === 'nsf-award' ? 'nsf' : 'reu'].loading && source !== 'rutgers' ? <DataSkeleton label="Loading research records" />
        : source !== 'rutgers' && model.optionalSources[source === 'nsf-award' ? 'nsf' : 'reu'].error ? <OperationalState kind="error" title="Research source unavailable" description="Try loading this source again, or switch to Rutgers labs." action={<Button variant="secondary" onClick={actions.retry}>Retry</Button>} />
        : <OperationalState kind="empty" title="No research opportunities match these filters" description={savedOnly ? 'Save a project using its bookmark, or turn off the saved filter to browse.' : 'Try a broader topic, another campus, or remove Paid or Credit to see more labs.'} action={<Button variant="secondary" onClick={() => { setSavedOnly(false); actions.clearFilters(); }}>Reset directory</Button>} />}
      {model.totalPages > 1 && !savedOnly ? <div className="flex justify-center border-t border-line pt-5"><Button variant="secondary" disabled={filters.page >= model.totalPages} onClick={() => actions.setFilter('page', filters.page + 1)}>{filters.page >= model.totalPages ? 'All results shown' : `Show ${Math.min(DIRECTORY_PAGE_SIZE, model.totalResults - model.results.length)} more`}</Button></div> : null}
    </section>
    <Link href="/research-outreach" className={`${buttonClassName({ variant: 'quiet' })} justify-self-start`}>Plan your research outreach <ChevronRightIcon className="h-4 w-4" aria-hidden="true" /></Link>
    <DetailDialog model={model} onClose={actions.closeDetail} />
  </OperationalPage>;
}

function DirectoryFilters({
  model,
  actions,
}: {
  model: ResearchOpportunitiesDirectoryViewModel;
  actions: DirectoryViewActions;
}) {
  const { filters, filterOptions } = model;
  return (
    <div className="space-y-4">
      {(!filters.sourceTab || filters.sourceTab === 'rutgers') ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DirectorySelect
          label="Department"
          value={filters.department}
          options={filterOptions.departments}
          onChange={(value) => actions.setFilter('department', value)}
        />
        <DirectorySelect
          label="Program type"
          value={filters.programType}
          options={filterOptions.programTypes}
          onChange={(value) => actions.setFilter('programType', value)}
        />
        <DirectorySelect
          label="Campus"
          value={filters.campus}
          options={filterOptions.campuses}
          onChange={(value) => actions.setFilter('campus', value)}
        />
        <DirectorySelect
          label="Subject"
          value={filters.subject}
          options={filterOptions.subjects}
          onChange={(value) => actions.setFilter('subject', value)}
        />
      </div> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {(!filters.sourceTab || filters.sourceTab === 'rutgers') ? <Field label="Compensation">
          {(props) => (
            <Select
              {...props}
              value={filters.compensation === null ? '' : String(filters.compensation)}
              onChange={(event) => actions.setFilter(
                'compensation',
                event.target.value === '' ? null : event.target.value === 'true',
              )}
            >
              <option value="">Any compensation</option>
              <option value="true">With compensation listed</option>
              <option value="false">No compensation listed</option>
            </Select>
          )}
        </Field> : null}
        <Field label="Advanced sort results">
          {(props) => (
            <Select
              {...props}
              value={filters.sortBy}
              onChange={(event) => actions.setFilter(
                'sortBy',
                event.target.value as DirectoryFilters['sortBy'],
              )}
            >
              <option value="relevance">Best match</option>
              <option value="title">Title</option>
              <option value="professor">Professor/contact</option>
              <option value="department">Department</option>
            </Select>
          )}
        </Field>
        <Field label="Sort order">
          {(props) => (
            <Select
              {...props}
              value={filters.sortOrder}
              onChange={(event) => actions.setFilter(
                'sortOrder',
                event.target.value as DirectoryFilters['sortOrder'],
              )}
            >
              <option value="asc">A–Z</option>
              <option value="desc">Z–A</option>
            </Select>
          )}
        </Field>
        <fieldset className="space-y-1 text-sm text-[var(--ss-text)]">
          <legend className="mb-1.5 text-sm font-medium">Sources and status</legend>
          <Toggle
            label="Include concluded records"
            checked={filters.includeConcluded}
            onChange={(value) => actions.setFilter('includeConcluded', value)}
          />
          <Toggle
            label="Include Rutgers REU projects"
            checked={filters.includeREU}
            onChange={(value) => actions.setFilter('includeREU', value)}
          />
        </fieldset>
      </div>
    </div>
  );
}

function DirectorySelect({
  label,
  value,
  options,
  onChange,
  compact = false,
}: {
  compact?: boolean;
  label: string;
  value: string;
  options: string[];
  onChange(value: string): void;
}) {
  return (
    <Field label={label} hideLabel={compact}>
      {(props) => (
        <Select {...props} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{compact ? label : `All ${label.toLowerCase()}s`}</option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </Select>
      )}
    </Field>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(value: boolean): void;
}) {
  return (
    <label className="flex min-h-12 cursor-pointer items-center gap-3 md:min-h-10">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 accent-[var(--ss-brand)]"
      />
      <span>{label}</span>
    </label>
  );
}

function OptionalSourceFeedback({ model }: { model: ResearchOpportunitiesDirectoryViewModel }) {
  const feedback: Array<{ label: string; tone: 'neutral' | 'warning' | 'info' }> = [];
  if (model.filters.includeNSF) {
    if (model.optionalSources.nsf.loading) feedback.push({ label: 'Loading NSF awards', tone: 'info' });
    if (model.optionalSources.nsf.error) feedback.push({ label: 'NSF awards unavailable', tone: 'warning' });
  }
  if (model.filters.includeREUSites) {
    if (model.optionalSources.reu.loading) feedback.push({ label: 'Loading NSF REU sites', tone: 'info' });
    if (model.optionalSources.reu.error) feedback.push({ label: 'NSF REU sites unavailable', tone: 'warning' });
  }
  if (!feedback.length) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-2" role="status" aria-live="polite">
      {feedback.map(({ label, tone }) => (
        <OperationalStatus key={label} tone={tone}>{label}</OperationalStatus>
      ))}
    </div>
  );
}

function OpportunityRow({ item, onOpen, onSave, saved, query }: {
  item: DirectoryOpportunity; onOpen(): void; onSave(): void; saved: boolean; query: string;
}) {
  const signals = compensationSignals(item.compensation);
  const summary = item.summary.replace(/^[\s●•▪◦–—*-]+/, '').replace(/\s+/g, ' ').trim();
  const titleIndex = query.trim() ? item.title.toLowerCase().indexOf(query.trim().toLowerCase()) : -1;
  return <article className="relative py-5 sm:py-6">
    <button type="button" onClick={onOpen} aria-label={`View details: ${item.title}`} className="group block min-h-12 w-full rounded-control pr-12 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ss-focus)]">
      <h3 className="text-base font-semibold leading-6 text-content group-hover:underline sm:text-lg">{titleIndex < 0 ? item.title : <>{item.title.slice(0, titleIndex)}<mark className="bg-[var(--ss-brand-bg)] text-content">{item.title.slice(titleIndex, titleIndex + query.trim().length)}</mark>{item.title.slice(titleIndex + query.trim().length)}</>}</h3>
      <p className="mt-1 text-sm text-content-muted">{item.professor} · {item.kind === 'reu-site' ? item.site?.institution : item.department}</p>
      {summary ? <p className="mt-3 line-clamp-2 text-sm leading-6 text-content-muted">{summary}</p> : null}
      <span className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-content-muted">
        {item.campus ? <span className="inline-flex items-center gap-1.5"><MapPinIcon className="h-4 w-4" aria-hidden="true" />{item.campus}</span> : null}
        {signals.paid ? <span className="inline-flex items-center gap-1.5"><CurrencyDollarIcon className="h-4 w-4" aria-hidden="true" />Paid</span> : null}
        {signals.credit ? <span className="inline-flex items-center gap-1.5"><AcademicCapIcon className="h-4 w-4" aria-hidden="true" />Credit option</span> : null}
        {item.project?.timelines.research_start ? <span className="inline-flex items-center gap-1.5"><CalendarDaysIcon className="h-4 w-4" aria-hidden="true" />{item.project.timelines.research_start}</span> : null}
        {item.concluded ? <OperationalStatus tone="warning">Concluded snapshot</OperationalStatus> : null}
        {item.kind !== 'rutgers' ? <OperationalStatus tone="info">{item.kind === 'nsf-award' ? 'NSF funding record' : 'REU program'}</OperationalStatus> : null}
      </span>
      <ChevronRightIcon className="absolute right-3 top-24 h-5 w-5 text-content-muted" aria-hidden="true" />
    </button>
    <button type="button" aria-label={`${saved ? 'Unsave' : 'Save'} ${item.title} on this device`} aria-pressed={saved} onClick={onSave} className="absolute right-0 top-3 flex h-12 w-12 items-center justify-center rounded-control text-content-muted hover:bg-surface">
      <BookmarkIcon className={`h-5 w-5 ${saved ? 'fill-current text-[var(--ss-brand)]' : ''}`} aria-hidden="true" />
    </button>
  </article>;
}

function DetailDialog({
  model,
  onClose,
}: {
  model: ResearchOpportunitiesDirectoryViewModel;
  onClose(): void;
}) {
  const item = model.selected;
  if (!item) return null;
  return (
    <Modal isOpen onClose={onClose} title={item.title} size="xl">
      <ResearchOpportunityDetails model={model} showTitle={false} />
    </Modal>
  );
}

export function ResearchOpportunityDetails({ model, showTitle = true }: { model: ResearchOpportunitiesDirectoryViewModel; showTitle?: boolean }) {
  const item = model.selected;
  if (!item) return <OperationalState kind="empty" title="Select a research opportunity" description="Evidence and next actions will stay beside the directory while you compare leads." />;
  return <div className="space-y-5"><div><p className="operational-eyebrow">{item.kind === 'rutgers' ? 'Rutgers opportunity' : item.kind === 'nsf-award' ? 'NSF award' : 'NSF REU site'}</p>{showTitle ? <h2 className="text-xl font-bold text-content">{item.title}</h2> : null}<p className="mt-1 text-sm text-content-muted">{item.professor} · {item.department}{item.campus ? ` · ${item.campus}` : ''}</p></div><DetailEvidence item={item} retrievedAt={model.retrievedAt} />{item.kind === 'rutgers' ? <RutgersDetails item={item} /> : null}{item.kind === 'reu-site' ? <REUSiteDetails item={item} /> : null}{item.kind === 'nsf-award' ? <NSFAwardDetails item={item} detail={model.nsfDetail} loading={model.loadingDetail} error={model.detailError} /> : null}<DetailActions item={item} /></div>;
}

function DetailEvidence({
  item,
  retrievedAt,
}: {
  item: DirectoryOpportunity;
  retrievedAt?: string;
}) {
  if (item.kind === 'rutgers') {
    return (
      <>
        <OperationalEvidence tone={item.concluded ? 'warning' : 'info'}>
          {item.concluded
            ? 'This is a concluded Rutgers snapshot, not a current listing.'
            : 'This is a checked-in Rutgers snapshot; confirm availability, deadlines, and eligibility with the contact.'}
        </OperationalEvidence>
        <OperationalSource
          kind="checked-in"
          source="Rutgers research opportunity snapshot"
          retrievedAt={retrievedAt || 'timestamp not recorded'}
        />
      </>
    );
  }
  if (item.kind === 'nsf-award') {
    return (
      <>
        <OperationalEvidence tone="warning">
          An NSF award is funding context, not confirmation of an available student opportunity.
        </OperationalEvidence>
        <OperationalSource
          kind="cached"
          source="NSF award record"
          retrievedAt={item.award?.scraped_date || 'source timestamp not provided'}
        />
      </>
    );
  }
  return (
    <>
      <OperationalEvidence tone="warning">
        This checked-in NSF REU site record may not reflect current dates or openings. Confirm on the source site.
      </OperationalEvidence>
      <OperationalSource
        kind="checked-in"
        source="NSF REU site CSV"
        retrievedAt={item.site?.record_last_updated_date || 'record date not provided'}
      />
    </>
  );
}

function RutgersDetails({ item }: { item: DirectoryOpportunity }) {
  const project = item.project!;
  return (
    <div className="space-y-5">
      <DetailFacts facts={[
        ['Project ID', String(project.project_id)],
        ['Professor', project.professor],
        ['Department', project.department],
        ['Program', project.program_type],
        ['Campus', project.project_details.campus],
        ['Building', project.project_details.building],
        ['Compensation', usefulValue(project.project_details.compensation)],
        ['Commitment', project.project_details.commitment],
        ['Availability', project.project_details.availability],
        ['Application type', project.project_details.application_type],
      ]} />
      <DetailText title="Description" value={project.full_description || project.summary} />
      <DetailText title="Applicant responsibilities" value={project.applicant_responsibilities} />
      <DetailText title="Mentoring plan" value={project.mentoring_plan} />
      <DetailFacts facts={[
        ['First date to apply', project.timelines.first_date_to_apply],
        ['Last date to apply', project.timelines.last_date_to_apply],
        ['Research start', project.timelines.research_start],
        ['Research end', project.timelines.research_end],
        ['Duration', project.timelines.duration],
      ]} />
      {project.advisors.length ? (
        <DetailList title="Advisors">
          {project.advisors.map((advisor, index) => (
            <li key={`${advisor.name}-${index}`}>
              <span className="font-medium text-[var(--ss-text)]">{advisor.name}</span>
              {advisor.role ? ` · ${advisor.role}` : ''}
              {advisor.email ? (
                <a className="ml-2 underline underline-offset-2" href={`mailto:${cleanEmail(advisor.email)}`}>
                  {advisor.email}
                </a>
              ) : null}
            </li>
          ))}
        </DetailList>
      ) : null}
      {project.requirements.length ? (
        <DetailList title="Requirements">
          {project.requirements.map((requirement, index) => (
            <li key={`${requirement.type}-${index}`}>
              <span className="font-medium text-[var(--ss-text)]">{requirement.type}:</span>{' '}
              {requirement.value}
            </li>
          ))}
        </DetailList>
      ) : null}
      <TagList title="Subject areas" values={project.subject_headings} />
    </div>
  );
}

function REUSiteDetails({ item }: { item: DirectoryOpportunity }) {
  const site = item.site!;
  const location = [site.institution_city, site.institution_state || site.site_location]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="space-y-5">
      <DetailFacts facts={[
        ['Institution', site.institution],
        ['Location', location],
        ['Additional location', site.additional_location_info],
        ['Department', site.institution_department],
        ['NSF award number', site.nsf_award_number],
        ['NSF funding unit', site.primary_nsf_funding_unit],
        ['NSF co-funders', site.nsf_co_funders],
        ['External co-funders', site.external_co_funders],
        ['Last updated', site.record_last_updated_date],
        ['Expiration date', site.record_expiration_date],
      ]} />
      <DetailText title="Site description" value={site.site_description} />
      <DetailText title="Research areas" value={site.research_areas} />
      <TagList title="Research topics" values={splitValues(site.research_topics_keywords)} />
      <DetailText title="Additional information" value={site.other_information} />
      <DetailText title="Comments" value={site.comments} />
      {(site.primary_contact_name || site.secondary_contact_name) ? (
        <DetailList title="Contacts">
          {site.primary_contact_name ? (
            <li>
              <span className="font-medium text-[var(--ss-text)]">{site.primary_contact_name}</span>
              {' · Primary contact'}
              {site.primary_contact_email ? (
                <a className="ml-2 underline underline-offset-2" href={`mailto:${cleanEmail(site.primary_contact_email)}`}>
                  {site.primary_contact_email}
                </a>
              ) : null}
              {site.primary_contact_phone ? ` · ${site.primary_contact_phone}` : ''}
            </li>
          ) : null}
          {site.secondary_contact_name ? (
            <li>
              <span className="font-medium text-[var(--ss-text)]">{site.secondary_contact_name}</span>
              {' · Secondary contact'}
              {site.secondary_contact_email ? (
                <a className="ml-2 underline underline-offset-2" href={`mailto:${cleanEmail(site.secondary_contact_email)}`}>
                  {site.secondary_contact_email}
                </a>
              ) : null}
              {site.secondary_contact_phone ? ` · ${site.secondary_contact_phone}` : ''}
            </li>
          ) : null}
        </DetailList>
      ) : null}
    </div>
  );
}

function NSFAwardDetails({
  item,
  detail,
  loading,
  error,
}: {
  item: DirectoryOpportunity;
  detail?: NSFAwardDetail;
  loading: boolean;
  error?: string;
}) {
  const award = detail ?? item.award!;
  return (
    <div className="space-y-5">
      {loading ? <OperationalState kind="loading" title="Loading NSF award detail" /> : null}
      {error ? (
        <OperationalState
          kind="partial"
          title="Detailed NSF record unavailable"
          description={`${error}. Summary award information remains available.`}
        />
      ) : null}
      <DetailFacts facts={[
        ['Award ID', award.id],
        ['Principal investigator', award.pdPIName],
        ['Institution', award.awardeeName || detail?.awardee],
        ['Performance location', award.perfLocation],
        ['Start date', award.startDate],
        ['End date', award.expDate],
        ['Funds obligated', formatCurrency(award.fundsObligatedAmt)],
        ['Estimated total', formatCurrency(detail?.estimatedTotalAmt)],
        ['Program', award.fundProgramName],
        ['Transaction type', detail?.transType],
      ]} />
      <DetailText title="Abstract" value={detail?.abstractText} />
      <DetailText title="Program description" value={award.program} />
      {detail?.poName ? (
        <DetailList title="Program officer">
          <li>
            <span className="font-medium text-[var(--ss-text)]">{detail.poName}</span>
            {detail.poEmail ? (
              <a className="ml-2 underline underline-offset-2" href={`mailto:${cleanEmail(detail.poEmail)}`}>
                {detail.poEmail}
              </a>
            ) : null}
            {detail.poPhone ? ` · ${detail.poPhone}` : ''}
          </li>
        </DetailList>
      ) : null}
    </div>
  );
}

function DetailFacts({ facts }: { facts: Array<[string, string | undefined]> }) {
  const present = facts.filter(([, value]) => Boolean(value));
  if (!present.length) return null;
  return (
    <dl className="grid gap-x-5 gap-y-3 text-sm sm:grid-cols-2">
      {present.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="font-medium text-[var(--ss-text)]">{label}</dt>
          <dd className="mt-1 break-words text-[var(--ss-text-muted)]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function DetailText({ title, value }: { title: string; value?: string }) {
  if (!value) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold text-[var(--ss-text)]">{title}</h3>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--ss-text-muted)]">{value}</p>
    </section>
  );
}

function DetailList({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-[var(--ss-text)]">{title}</h3>
      <ul className="mt-2 space-y-2 text-sm leading-6 text-[var(--ss-text-muted)]">{children}</ul>
    </section>
  );
}

function TagList({ title, values }: { title: string; values: string[] }) {
  if (!values.length) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold text-[var(--ss-text)]">{title}</h3>
      <div className="mt-2 flex flex-wrap gap-2">
        {values.map((value) => <OperationalStatus key={value}>{value}</OperationalStatus>)}
      </div>
    </section>
  );
}

function DetailActions({ item }: { item: DirectoryOpportunity }) {
  const email = item.kind === 'rutgers'
    ? item.project?.advisors.find((advisor) => advisor.email)?.email
    : item.kind === 'nsf-award'
      ? item.award?.piEmail
      : item.site?.primary_contact_email;
  const external = safeExternalHref(item.kind === 'nsf-award'
    ? `https://www.nsf.gov/awardsearch/showAward?AWD_ID=${encodeURIComponent(item.award!.id.split(',')[0].trim())}`
    : item.kind === 'reu-site'
      ? item.site?.site_website
      : item.project?.project_details.website);


  return (
    <div className="flex flex-wrap gap-3 border-t border-[var(--ss-border)] pt-4">
      {email ? (
        <a
          className={`${buttonClassName({ variant: 'primary' })} min-h-12`}
          href={`mailto:${cleanEmail(email)}?subject=${encodeURIComponent(`Research interest: ${item.title}`)}`}
        >
          Email contact
        </a>
      ) : null}
      <Link className={buttonClassName({ variant: 'secondary' })} href="/research-outreach">Draft research outreach</Link>
      {external ? (
        <a
          className={`${buttonClassName({ variant: 'secondary' })} min-h-12`}
          href={external}
          target="_blank"
          rel="noreferrer"
        >
          Open source
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      ) : null}
    </div>
  );
}

function countActiveFilters(filters: DirectoryFilters) {
  return [
    filters.department,
    filters.programType,
    filters.campus,
    filters.subject,
    filters.compensation !== null,
    filters.paidOnly,
    filters.creditOnly,
    filters.includeConcluded,
    filters.includeREU,
  ].filter(Boolean).length;
}

function usefulValue(value?: string) {
  return value && value !== 'N/A' ? value : undefined;
}

function cleanEmail(value: string) {
  return value.replace(/[\r\n]/g, '').trim();
}

function safeExternalHref(value?: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function splitValues(value?: string) {
  return value?.split(',').map((item) => item.trim()).filter(Boolean) ?? [];
}

function formatCurrency(value?: number) {
  if (!value || value <= 0) return undefined;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}
